import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Button,
  Chip,
  styled
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  GetApp as FileDownloadIcon,
  TravelExplore as TravelExploreIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material';
import { getS3UrlByKeyBase64, downloadOnePagerByKey } from '../../../API/API';
import { toast } from 'react-toastify';

const StatusChip = styled(Chip)(({ status }) => {
  const statusConfig = {
    "Pending": { color: "#FFA000", bg: "#FFF8E1" },
    "pending": { color: "#FFA000", bg: "#FFF8E1" },
    "incomplete": { color: "#FFA000", bg: "#FFF8E1" },
    "Rejected": { color: "#F44336", bg: "#FFEBEE" },
    "rejected": { color: "#F44336", bg: "#FFEBEE" },
    "Processing": { color: "#2196F3", bg: "#E3F2FD" },
    "processing": { color: "#2196F3", bg: "#E3F2FD" },
    "Submitted": { color: "#4CAF50", bg: "#E8F5E9" },
    "submitted": { color: "#4CAF50", bg: "#E8F5E9" },
    "New": { color: "#0052CC", bg: "#E5F6FF" }
  };

  const config = statusConfig[status] || { color: "#4CAF50", bg: "#E8F5E9" };

  return {
    color: config.color,
    backgroundColor: config.bg,
    fontWeight: 600,
    fontSize: "12px",
    height: "24px"
  };
});

const CentralizedTable = ({
  data = [],
  columns = [],
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  totalRecords,
  onDownloadReport,
  onDownloadDocument,
  onViewOffers,
  onTrackApplication,
  onViewApplication,
  onEditApplication,
  onUploadDocs,
  onCreateProposal,
  onViewProposal,
  onUpdateProposal,
  onSelectAction,
  renderActions,
  showReports = false,
  showOffers = false,
  showActions = true,
  hover = true,
  minWidth = "650px",
  StatusChip: CustomStatusChip,
  pdfRefs,
  onDownload,
  onSort,
  currentSortBy,
  currentOrder,
  compactActionColumns = false,
  ...props
}) => {
  const theme = useTheme();
  const isIndividualBorrower = localStorage.getItem('userBusinessType')?.toLowerCase() === 'individual';

  const encodeS3KeyToBase64 = (rawKey) => {
    const binary = encodeURIComponent(String(rawKey ?? "")).replace(
      /%([0-9A-F]{2})/g,
      (_, hex) => String.fromCharCode(parseInt(hex, 16))
    );
    return btoa(binary);
  };

  const normalizeS3Key = (value) => {
    if (!value || value?.constructor !== String) return "";
    if (!/^https?:\/\//i.test(value)) return value;

    try {
      const url = new URL(value);
      return decodeURIComponent(url.pathname.replace(/^\/+/, ""));
    } catch {
      return value;
    }
  };

  const getReportSource = (row, type) => {
    const sourcesByType = {
      onePager: [
        row?.proposalDetails?.onePager,
        row?.proposalDetails?.applicationForm?.onePager,
      ],
      cma: [
        row?.proposalDetails?.cma,
        row?.proposalDetails?.applicationForm?.cma,
      ],
    };

    return (sourcesByType[type] || []).find((value) => value) || "";
  };

  const fetchPresignedUrl = async (rawValue) => {
    const normalizedKey = normalizeS3Key(rawValue);
    if (!normalizedKey) return null;
    try {
      const response = await getS3UrlByKeyBase64(encodeS3KeyToBase64(normalizedKey));
      const candidates = [
        response?.result?.url,
        response?.url,
        response?.presignedUrl,
        response?.result,
        response?.data,
      ];

      return candidates.find(
        (value) => value?.constructor === String && value.length
      ) || null;
    } catch (error) {
      toast.error("Failed to fetch S3 URL:", error);
      return null;
    }
  };

  const handleReportDownload = async (row, type) => {
    const rawValue = getReportSource(row, type);
    if (!rawValue) return;

    const normalizedKey = normalizeS3Key(rawValue);

    if (type === "onePager") {
      if (!normalizedKey) return;

      try {
        const blob = await downloadOnePagerByKey(normalizedKey);
        const contentType = "application/pdf";
        const blobUrl = window.URL.createObjectURL(
          new Blob([blob], { type: contentType })
        );

        window.open(blobUrl, "_blank");
      } catch (error) {
        toast.error("Failed to download One Pager. Please try again.");
      }

      return;
    }

    const presignedUrl = await fetchPresignedUrl(rawValue);
    const urlToUse = presignedUrl || rawValue;
    if (!urlToUse) return;

    window.open(urlToUse, "_blank");
  };
  const defaultColumns = [
    { key: 'id', title: 'ID', type: 'text' },
    { key: 'name', title: 'Name', type: 'text' },
    { key: 'status', title: 'Status', type: 'status' }
  ];

  const tableColumns = columns.length > 0 ? columns : defaultColumns;

  const handleSort = (columnKey) => {
    if (!onSort) return;
    let direction = 'asc';
    if (currentSortBy === columnKey && currentOrder === 'asc') {
      direction = 'desc';
    }
    onSort(columnKey, direction);
  };

  const getSortIcon = (columnKey) => {
    if (!onSort || currentSortBy !== columnKey) {
      return null;
    }
    return currentOrder === 'asc' 
      ? <ArrowUpwardIcon sx={{ fontSize: '0.875rem', color: theme.palette.primary.main }} />
      : <ArrowDownwardIcon sx={{ fontSize: '0.875rem', color: theme.palette.primary.main }} />;
  };

  const renderCellContent = (row, column) => {
    const value = row[column.key];
    
    switch (column.type) {
      case 'maskedBorrower': {
        const entityName = value ? String(value) : "";
        if (!entityName) return "N/A";
        const normalizedStatus = String(row?.status || "").toLowerCase();
        if (normalizedStatus === 'accepted') return entityName;
        const entityNameLength = entityName.length;
        if (entityNameLength <= 2) {
          return entityName[0] + '*'.repeat(Math.max(0, entityNameLength - 1));
        }
        const firstCharacter = entityName[0];
        const lastCharacter = entityName[entityNameLength - 1];
        const maskedMiddle = '*'.repeat(entityNameLength - 2);
        return `${firstCharacter}${maskedMiddle}${lastCharacter}`;
      }
      case 'status':
        const StatusComponent = CustomStatusChip || StatusChip;
        return <StatusComponent label={value || "Unknown"} status={value || "Unknown"} size="small" />;
      
      case 'currency':
        if (!value) return "Not specified";
        const numericValue = typeof value === 'string' ? parseFloat(value) : value;
        return isNaN(numericValue) ? "Not specified" : `₹${numericValue.toLocaleString()}`;
      
      case 'date':
        return value ? new Date(value).toLocaleDateString() : "N/A";
      
      case 'proposalId':
        return value || "N/A";
      
      default:
        return value || "N/A";
    }
  };

  const renderReportsColumn = (row) => {
    if (!showReports) return null;

    const onePagerValue = getReportSource(row, 'onePager');
    const cmaValue = getReportSource(row, 'cma');

    return (
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          // disabled={!onePagerValue}
          disabled={!onePagerValue || isIndividualBorrower}
          onClick={() => handleReportDownload(row, 'onePager')}
          sx={{ textTransform: "none", opacity: onePagerValue ? 1 : 0.5 }}
        >
          One Pager
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          // disabled={!cmaValue}
          disabled={!cmaValue || isIndividualBorrower}
          onClick={() => handleReportDownload(row, 'cma')}
          sx={{ textTransform: "none", opacity: cmaValue ? 1 : 0.5 }}
        >
          CMA Report
        </Button>
      </Box>
    );
  };

  const renderOffersColumn = (row) => {
    if (!showOffers) return null;

    const offers = Array.isArray(row?.offerForProposal) ? row.offerForProposal : [];
    const lenderNames = offers.map((o) => o?.name).filter(Boolean);

    if (lenderNames.length === 0) {
      return <Box sx={{ color: "text.secondary", fontSize: 12 }}>No offers</Box>;
    }

    return (
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {offers.map((offer, idx) => (
          <Chip
            key={`${offer?.name || 'offer'}-${idx}`}
            label={offer?.name || 'Offer'}
            size="small"
            variant="outlined"
            onClick={onViewOffers ? () => onViewOffers(row, offer) : undefined}
            sx={{ cursor: onViewOffers ? 'pointer' : 'default' }}
          />
        ))}
      </Box>
    );
  };

  const renderActionsColumn = (row) => {
    if (!showActions) return null;

    if (renderActions) {
      const downloadBothReports = () =>
        handleReportDownload(row, 'onePager').then(() => handleReportDownload(row, 'cma'));
      return renderActions(row, { downloadBothReports });
    }

    return (
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap", alignItems: "center",}}>
        {onTrackApplication && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<TravelExploreIcon />}
            onClick={() => onTrackApplication(row)}
            sx={{ textTransform: "none" }}
          >
            Track
          </Button>
        )}
        {onViewApplication && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => onViewApplication(row)}
            sx={{ textTransform: "none" }}
          >
            View
          </Button>
        )}
        {onEditApplication && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => onEditApplication(row)}
            disabled={String(row?.status || "").toLowerCase() === "offered"}
            sx={{ textTransform: "none" }}
          >
            Edit
          </Button>
        )}
        {onCreateProposal && !row.proposalDetails && (
          <Button
            variant="contained"
            size="small"
            onClick={() => onCreateProposal(row)}
            sx={{ textTransform: "none" }}
          >
            Create Proposal
          </Button>
        )}
        {onViewProposal && row.proposalDetails && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => onViewProposal(row)}
            sx={{ textTransform: "none" }}
          >
            View Proposal
          </Button>
        )}
        {onUpdateProposal && row.proposalDetails && (
          <Button
            variant="contained"
            size="small"
            onClick={() => onUpdateProposal(row)}
            sx={{ textTransform: "none" }}
          >
            Update Proposal
          </Button>
        )}
        {onSelectAction && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => onSelectAction(row)}
            sx={{ textTransform: "none" }}
          >
            Select Action
          </Button>
        )}
        {onDownload && (
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => onDownload(row.id)}
            sx={{ textTransform: "none" }}
          >
            Download
          </Button>
        )}
      </Box>
    );
  };

  const getColumnSizing = (column = {}) => {
    const { minWidth, maxWidth, width } = column;
    return {
      minWidth: minWidth || undefined,
      maxWidth: maxWidth || undefined,
      width: width || maxWidth || minWidth || undefined,
    };
  };

  const compactHeaderStyles = {
    fontWeight: 600,
    backgroundColor: "#F5F5F5",
    cursor: onSort ? "pointer" : "default",
    userSelect: "none",
    py: 1,
    px: 1.25,
    fontSize: "0.85rem",
    lineHeight: 1.2,
  };

  const compactBodyStyles = {
    py: 0.75,
    px: 1.25,
    fontSize: "0.86rem",
    lineHeight: 1.35,
  };

  const reportsColumnSizing = compactActionColumns
    ? { width: 80, minWidth: 50, maxWidth: 100 }
    : { width: 150, minWidth: 140, maxWidth: 190 };

  const actionsColumnSizing = compactActionColumns
    ? { width: 80, minWidth: 50, maxWidth: 100 }
    : { width: 165, minWidth: 150, maxWidth: 210 };

  const renderMaterialTable = () => (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 'none', overflowX: "auto", overflowY: "hidden" }}>
        <Table stickyHeader aria-label="sticky table" sx={{ minWidth: minWidth }}>
          <TableHead>
            <TableRow>
              {tableColumns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || "left"}
                  sx={{
                    ...compactHeaderStyles,
                    ...getColumnSizing(column),
                    '&:hover': onSort ? {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    } : {},
                  }}
                  onClick={() => onSort && handleSort(column.key)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {column.title}
                    {getSortIcon(column.key)}
                  </Box>
                </TableCell>
              ))}
              {showReports && (
                <TableCell
                  align="center"
                  sx={{ ...compactHeaderStyles, ...reportsColumnSizing }}
                >
                  Reports
                </TableCell>
              )}
              {showOffers && (
                <TableCell align="center" sx={{ ...compactHeaderStyles }}>
                  Offers
                </TableCell>
              )}
              {showActions && (
                <TableCell
                  align="right"
                  sx={{ ...compactHeaderStyles, ...actionsColumnSizing }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow
                key={row.id || index}
                hover={hover}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '& td': { borderBottomColor: alpha(theme.palette.divider, 0.4) },
                  height: 56,
                }}
              >
                {tableColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align || "left"}
                    sx={{ ...compactBodyStyles, ...getColumnSizing(column) }}
                  >
                    {renderCellContent(row, column)}
                  </TableCell>
                ))}
                {showReports && (
                  <TableCell
                    align="center"
                    sx={{ ...compactBodyStyles, ...reportsColumnSizing }}
                  >
                    {renderReportsColumn(row)}
                  </TableCell>
                )}
                {showOffers && (
                  <TableCell align="center" sx={compactBodyStyles}>
                    {renderOffersColumn(row)}
                  </TableCell>
                )}
                {showActions && (
                  <TableCell
                    align="right"
                    sx={{ ...compactBodyStyles, ...actionsColumnSizing }}
                  >
                    {renderActionsColumn(row)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {data.length > 0 && (
        <TablePagination
          component="div"
          count={totalRecords || data.length}
          page={page}
          onPageChange={onPageChange || (() => {})}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange || (() => {})}
          rowsPerPageOptions={[5, 10, 25, 50, 100, { label: 'All', value: -1 }]}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      )}
    </Paper>
  );

  return renderMaterialTable();
};

export default CentralizedTable;